import { useMemo } from "react";
import { ScrollArea, Stack, Table, Text } from "@mantine/core";
import type { LegacyExtractionSummary } from "../../../types/legacyCases";
import {
  applyLegacyReportText,
  LEGACY_NARRATIVES,
  type LegacyReviewValues,
} from "../../../utils/legacyReviewForm";

interface Props {
  values: LegacyReviewValues;
  extraction?: LegacyExtractionSummary;
}

export default function LegacyExtractionComparison({
  values,
  extraction,
}: Props) {
  const changes = useMemo(() => {
    if (!extraction) return [];
    const proposed = applyLegacyReportText(values, extraction);
    return LEGACY_NARRATIVES.filter(
      ({ field }) => values[field] !== proposed[field]
    ).map(({ field, label }) => ({
      field,
      label,
      current: values[field],
      proposed: proposed[field],
    }));
  }, [values, extraction]);
  return (
    <Stack gap="sm">
      <Text size="sm">
        Compare your current draft with the selected extraction. Original source
        text remains available in the source evidence.
      </Text>
      {changes.length === 0 ? (
        <Text size="sm">
          The named narrative fields are unchanged. Additional sections and the
          complete transcript will use the selected extraction.
        </Text>
      ) : (
        <ScrollArea.Autosize mah={360} type="auto">
          <Table.ScrollContainer minWidth={400}>
            <Table verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Field</Table.Th>
                  <Table.Th>Current draft</Table.Th>
                  <Table.Th>Selected extraction</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {changes.map((change) => (
                  <Table.Tr key={change.field}>
                    <Table.Td>{change.label}</Table.Td>
                    <Table.Td>
                      <Text
                        size="sm"
                        style={{
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {change.current || "Empty"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text
                        size="sm"
                        style={{
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {change.proposed || "Empty"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </ScrollArea.Autosize>
      )}
    </Stack>
  );
}
